/**
 * authState — short-lived localStorage signal for completed email/OTP verification.
 *
 * Set once the user confirms their OTP (or skips verification into a real
 * session) so the app knows this browser just completed verification. Used to
 * gracefully handle returns from external redirects (e.g. Stripe checkout):
 * a verified user bounced back onto an onboarding URL should resume in-product
 * rather than restart the wizard.
 *
 * TTL: 5 minutes — long enough to cover the sign-up → onboarding → Stripe
 * round-trip, short enough that it never lingers as a stale signal. It is
 * re-set on every successful verification, so it's self-healing.
 */

const OTP_CONFIRMED_KEY = "humblytics:otp_confirmed:v1";
const TTL_MS = 5 * 60 * 1000; // 5 minutes

/** Call immediately after a successful OTP verification / verified sign-up. */
export function markOtpConfirmed(): void {
  try {
    localStorage.setItem(OTP_CONFIRMED_KEY, String(Date.now() + TTL_MS));
  } catch {
    // Ignore storage errors.
  }
}

/**
 * True if this browser completed OTP verification within the TTL window.
 * Expired entries are cleaned up on read.
 */
export function hasConfirmedOtp(): boolean {
  try {
    const raw = localStorage.getItem(OTP_CONFIRMED_KEY);
    if (!raw) return false;
    const expiresAt = parseInt(raw, 10);
    if (Number.isNaN(expiresAt) || Date.now() > expiresAt) {
      localStorage.removeItem(OTP_CONFIRMED_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Clear the flag (e.g. on explicit logout). */
export function clearOtpConfirmed(): void {
  try {
    localStorage.removeItem(OTP_CONFIRMED_KEY);
  } catch {
    // Ignore storage errors.
  }
}
